import { FC } from "react";
import { Collapse } from "antd";
import cn from "classnames";
import { MdKeyboardArrowDown } from "react-icons/md";
import { PASSENGER_FIELDS, PASSENGER_TYPE_ICONS, PassengerFieldPlacement, PassengerType } from "../constants";
import { useTranslation } from "react-i18next";

const getFields = (passenger: PassengerType, placement: PassengerFieldPlacement) =>
  PASSENGER_FIELDS.filter((field) => field.placement === placement)
    .map((field) => ({ ...field, value: field.render(passenger) }))
    .filter(({ value }) => value != null);

const PassengerCard: FC<{ passenger: PassengerType }> = ({ passenger }) => {
  const mainFields = getFields(passenger, "main");
  const sideFields = getFields(passenger, "side");
  const bottomFields = getFields(passenger, "bottom");

  return (
    <div className="bg-background-default outline-border-subtle flex flex-col overflow-hidden rounded-xl outline">
      <div className="flex">
        <div
          className={cn(
            "text-icon-inverted flex w-20 shrink-0 items-center justify-center md:w-24",
            passenger.isLeadPax ? "bg-background-primary" : "bg-background-secondary",
          )}
        >
          {PASSENGER_TYPE_ICONS[passenger.type] ?? PASSENGER_TYPE_ICONS.Adult}
        </div>
        <div className="flex flex-1 flex-wrap items-center justify-between gap-4 p-4">
          <div className="flex flex-col gap-1">
            <div className="text-font-minimal text-xs">{mainFields.find(({ title }) => title)?.title}</div>
            <div className="flex flex-wrap items-center gap-3">
              {mainFields.map(({ key, title, value }) => (
                <div key={key} className={cn(title && "text-xl font-bold")}>
                  {value}
                </div>
              ))}
            </div>
          </div>
          <div className="border-border-default flex gap-8 md:border-s md:border-dashed md:ps-6">
            {sideFields.map(({ key, title, value }) => (
              <div key={key} className="flex flex-col justify-center gap-1">
                {title && <div className="text-font-minimal text-xs">{title}</div>}
                <div className="font-semibold">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {bottomFields.length > 0 && (
        <div className="border-border-subtle bg-background-minimal flex flex-wrap gap-3 border-t px-4 py-3">
          {bottomFields.map(({ key, icon, value }) => (
            <div
              key={key}
              className="border-border-default bg-background-default flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium"
            >
              <span className="text-font-active">{icon}</span>
              <span dir="ltr">{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PassengerData: FC<{ passengers: PassengerType[] }> = ({ passengers }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-background-default flex flex-col rounded-sm shadow-sm p-5">
      <Collapse
        ghost
        expandIconPlacement="end"
        defaultActiveKey={"collapse-1"}
        expandIcon={({ isActive }) => (
          <span className="border-border-default text-icon-default flex size-8 items-center justify-center rounded-md!">
            <MdKeyboardArrowDown className={cn("size-5 transition-transform", isActive && "rotate-180")} />
          </span>
        )}
        classNames={{
          header: "items-center! p-0!",
          body: "p-0! py-3!",
        }}
        items={[
          {
            label: <div className="text-font-default text-sm font-semibold md:text-base">{t("UserHistory.flight.passengerDetails")}</div>,
            key: "collapse-1",
            children: (
              <div className="flex flex-col gap-3">
                {passengers.map((passenger, index) => (
                  <PassengerCard key={`passenger-${index}`} passenger={passenger} />
                ))}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default PassengerData;
